import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import { SubHeader } from "./SubHeader";
import { Footer } from "./Footer";
import { useLang } from "@/lib/i18n";
import { PHONE, PHONE_LABEL } from "@/lib/api";
import { usePrices, allItems, quoteUrl } from "@/lib/prices";
import { offer } from "@/lib/priceSchema";
import { useSeo, useJsonLd } from "@/lib/seo";

const UI = {
  no: { price: "Pris inkl. mva.", cta: "Få tilbud", all: "Se hele prislisten", call: "Ring oss", note: "Veiledende priser fra prislisten vår. Endelig pris bekreftes for din bil før arbeidet starter." },
  en: { price: "Price incl. VAT", cta: "Get a quote", all: "See the full price list", call: "Call us", note: "Indicative prices from our price list. The final price is confirmed for your car before the work starts." },
};

const Section = ({ s, i, items, lang, ui }) => {
  const nav = useNavigate();
  const label = items[0] ? `${items[0].name_no} (${items[0].price})` : s.labelNo;
  return (
    <section id={s.id} className="grid gap-6 border-t border-white/10 py-12 md:grid-cols-12 md:gap-10" data-testid={`service-section-${s.id}`}>
      <div className="md:col-span-5">
        <span className="font-mono text-xs text-[var(--red)]">/0{i + 1}</span>
        <h2 className="mt-2 font-display text-3xl font-black uppercase leading-none md:text-4xl">{s.title}</h2>
      </div>
      <div className="md:col-span-7">
        <p className="text-base leading-relaxed text-zinc-300">{s.body}</p>
        {s.link && (
          <Link to={s.link.to} data-testid={`service-link-${s.id}`} className="mt-3 inline-flex items-center gap-2 text-sm text-white underline decoration-[var(--red)] underline-offset-4 hover:text-zinc-200">
            {s.link.label} <ArrowUpRight size={14} />
          </Link>
        )}
        {items.length > 0 && (
          <div className="mt-6 border-t border-white/20" data-testid={`service-prices-${s.id}`}>
            {items.map((it) => (
              <div key={it.name_no} className="flex items-baseline justify-between gap-4 border-b border-white/10 py-3">
                <span className="text-sm text-zinc-200">{(lang === "en" && it.name_en) || it.name_no}</span>
                <span className="whitespace-nowrap font-display text-xl font-bold text-white" data-testid="service-price-value">{it.price}</span>
              </div>
            ))}
            <div className="pt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{ui.price}</div>
          </div>
        )}
        <button type="button" onClick={() => nav(quoteUrl(s.service, label))} data-testid={`service-quote-btn-${s.id}`}
          className="group mt-6 inline-flex items-center gap-2 border border-[var(--red)] px-5 py-3 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[var(--red)]">
          {ui.cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </section>
  );
};

// Shared layout for /chiptuning and /bilnokler. Prices come from the admin price list via each section's `match`.
export const ServicePage = ({ content, path, testid }) => {
  const { lang } = useLang();
  const t = content[lang] ?? content.no;
  const ui = UI[lang] ?? UI.no;
  const { data } = usePrices();
  const items = allItems(data);
  const pick = (s) => items.filter(s.match);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useSeo({ title: t.docTitle, desc: t.desc, path });
  const offers = content.no.sections.flatMap((s) => pick(s)).map(offer).filter(Boolean);
  useJsonLd(`${testid}-jsonld`, data && {
    "@context": "https://schema.org", "@type": "Service", name: content.no.h1, serviceType: content.no.h1, url: `https://rbtun.no${path}`,
    provider: { "@id": "https://rbtun.no/#business" }, areaServed: ["Stavanger", "Sandnes", "Rogaland"],
    ...(offers.length ? { hasOfferCatalog: { "@type": "OfferCatalog", name: content.no.h1, itemListElement: offers } } : {}),
  });
  return (
    <div data-testid={testid}>
      <SubHeader />
      <main className="mx-auto max-w-5xl px-5 py-16 sm:px-8 md:py-24">
        <div className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-[var(--red)]">{t.eyebrow}</div>
        <h1 className="font-display text-5xl font-black uppercase leading-[0.9] md:text-7xl">{t.h1a} <span className="text-[var(--red)]">{t.h1b}</span></h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-300 md:text-lg" data-testid="service-intro">{t.intro}</p>
        <div className="mt-14">
          {t.sections.map((s, i) => <Section key={s.id} s={{ ...s, labelNo: content.no.sections[i].title }} i={i} items={pick(content.no.sections[i])} lang={lang} ui={ui} />)}
        </div>
        <p className="mt-4 border-l-2 border-[var(--red)] pl-4 text-sm leading-relaxed text-zinc-300">{ui.note}</p>
        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          <Link to="/priser" data-testid="service-prices-link" className="group flex items-center justify-between gap-4 bg-[var(--red)] p-6">
            <span className="font-display text-2xl font-black uppercase leading-none">{ui.all}</span>
            <ArrowUpRight size={26} className="shrink-0 transition-transform duration-500 group-hover:rotate-45" />
          </Link>
          <Link to={t.other.to} data-testid="service-other-link" className="group flex items-center justify-between gap-4 border border-white/15 p-6 transition-colors hover:border-white/50">
            <span className="font-display text-2xl font-black uppercase leading-none">{t.other.label}</span>
            <ArrowUpRight size={26} className="shrink-0 transition-transform duration-500 group-hover:rotate-45" />
          </Link>
        </div>
        <a href={`tel:${PHONE}`} className="mt-10 inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-zinc-200 hover:text-white" data-testid="service-phone">
          <Phone size={16} className="text-[var(--red)]" /> {ui.call}: {PHONE_LABEL}
        </a>
      </main>
      <Footer />
    </div>
  );
};
